import { FC } from 'react';
import { config } from 'config';
import { Ipn, UpdateIpnData } from 'types/ipn.types';
import { IdentifierVerificationResponse, Link } from 'types/api.types';
import { useAppSelector, useAppDispatch } from 'app/store';
import { openURL } from 'app/utils/utils';
import { useFetch, useResource } from 'app/utils/hooks';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  addIpnManuallyEffect,
  fetchCmoSocietiesEffect,
  fetchIpnValidationsEffect,
  updateIpnEffect,
} from 'app/store/Profile/effects';
import { initiateV2VerificationAction } from 'app/store/Mixpanel/slice';
import {
  getAddIpnV2LinkSelector,
  getCmoSocieties,
  getCmoSocietiesListLink,
  getIpnValidations,
} from 'app/store/Profile/selectors';
import { SocietyOption } from 'app/components/Profile/Ids/types';
import { IpnForm } from './types';
import { IpnDialogComponent } from './IpnDialogComponent';
import { toAddIpnData } from './utils';

interface IpnDialogProps {
  open: boolean;
  ipn?: Ipn;
  onV3Verification: (data: SocietyOption) => void;
  onClose: (status?: number) => void;
}

export const IpnDialog: FC<IpnDialogProps> = ({
  open,
  ipn,
  onV3Verification,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const canFetchSocieties = Boolean(useAppSelector(getCmoSocietiesListLink));
  const addIpnV2Link = useAppSelector(getAddIpnV2LinkSelector);
  const { callAPI: addIpnV2 } = useFetch<
    { societyId: string },
    IdentifierVerificationResponse
  >();
  const [cmoSocieties, cmoSocietiesLoading] = useResource(
    fetchCmoSocietiesEffect,
    getCmoSocieties,
    {
      shouldFetch: open && canFetchSocieties,
    }
  );

  const [ipnValidations] = useResource(
    fetchIpnValidationsEffect,
    getIpnValidations,
    {
      shouldFetch: open && Boolean(cmoSocieties),
    }
  );

  const addIpnManually = (data: Partial<IpnForm>) =>
    dispatch(
      addIpnManuallyEffect({
        formData: toAddIpnData(data),
        societyTitle: data.society?.title || '',
      })
    ).unwrap();

  const updateIpn = (formData: Partial<UpdateIpnData>, link?: Link) =>
    dispatch(updateIpnEffect({ formData, link })).unwrap();

  const handleAddIpnV2 = async (values: IpnForm) => {
    try {
      if (!values.society) return;
      const { response } = await addIpnV2(addIpnV2Link?.href || '', {
        societyId: values.society.id,
      });
      dispatch(initiateV2VerificationAction(values.society.title));
      openURL(response.identifierVerification._links.redirect.href, {
        isExternal: true,
      });
      if (config.isElectronBuild) {
        onClose();
      }
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
    }
  };

  return (
    <IpnDialogComponent
      open={open}
      societies={cmoSocieties || []}
      areSocietiesLoading={cmoSocietiesLoading}
      ipn={ipn}
      ipnValidations={ipnValidations}
      updateIpn={updateIpn}
      addIpnManually={addIpnManually}
      handleAddIpnV2={handleAddIpnV2}
      handleAddIpnV3={onV3Verification}
      onClose={onClose}
    />
  );
};
